/* InterventionQueuePage.jsx — operational intervention surface.
 *
 * The dedicated, calm operational queue prioritising:
 *   • fee at risk
 *   • exchange probability
 *   • blocker severity
 *   • inactivity duration
 *   • chain fragility
 *   • overdue milestones
 *   • participant responsiveness
 *
 * Reuses the canonical /api/interventions/queue endpoint — single
 * source of truth, never re-derived client-side. Engine returns items
 * pre-ranked by score (-fee × urgency × severity).
 *
 * NOT a task list. NOT a CRM feed. NOT a dashboard panel.
 * This is the operational layer that turns ChainFlow from
 * "software that tracks deals" into "an operational system actively
 *  protecting transaction outcomes".
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldAlert, AlertOctagon, Clock, FileWarning, Loader2,
  ArrowRight, Check, Filter, Sparkles, ShieldCheck,
} from "lucide-react";

import { Sidebar } from "../components/Sidebar";
import { api } from "../lib/api";
import { ChaseLadderSection } from "../components/chase-ladder/ChaseLadderSection";


/* ─── Visual hierarchy ───────────────────────────────────────────── */

const URGENCY_TONE = {
  critical: {
    fg: "#9F1239",
    bg: "rgba(244,63,94,0.10)",
    border: "rgba(244,63,94,0.30)",
    label: "Critical",
  },
  important: {
    fg: "#92400E",
    bg: "rgba(245,158,11,0.10)",
    border: "rgba(245,158,11,0.30)",
    label: "Important",
  },
  informational: {
    fg: "#3730A3",
    bg: "rgba(99,102,241,0.10)",
    border: "rgba(99,102,241,0.28)",
    label: "Informational",
  },
};

const CATEGORY_ICON = {
  inactive_solicitor:  AlertOctagon,
  document_blockers:   FileWarning,
  mortgage_risk:       ShieldAlert,
  overdue_milestone:   Clock,
  slipping_exchange:   AlertOctagon,
  completion_drift:    Clock,
  fragile_chain:       ShieldAlert,
  exchange_inactivity: Clock,
  downstream_casualty: ShieldAlert,
};


const _money = (n) => {
  if (n == null) return null;
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `£${Math.round(v / 1_000)}K`;
  return `£${Math.round(v).toLocaleString()}`;
};


/* ─── Hero — revenue protection summary ────────────────────────── */

const QueueHero = ({ total, feeAtRiskTotal, criticalCount, importantCount, informationalCount }) => {
  const feeLabel = _money(feeAtRiskTotal) || "£0";

  return (
    <header
      className="relative overflow-hidden rounded-3xl mb-6"
      style={{
        background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 100%)",
        boxShadow: "0 24px 60px -28px rgba(15,23,42,0.55)",
      }}
      data-testid="intervention-queue-hero"
    >
      <span
        aria-hidden="true"
        className="absolute top-0 right-0 w-[420px] h-[280px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(closest-side, rgba(139,92,246,0.25), transparent 70%)" }}
      />
      <div className="relative grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-5 px-6 md:px-9 py-7">
        <div>
          <div className="flex items-center gap-2 mb-2.5">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.18em]"
              style={{ color: "#A78BFA", background: "rgba(139,92,246,0.16)" }}
            >
              <ShieldCheck size={11} strokeWidth={2.4} />
              Revenue protection
            </span>
          </div>
          <h1
            className="text-[26px] md:text-[30px] font-bold tracking-tight leading-tight"
            style={{ color: "#FFFFFF" }}
            data-testid="intervention-queue-title"
          >
            Intervention queue
          </h1>
          <p
            className="mt-2 text-[13.5px] max-w-[480px]"
            style={{ color: "rgba(226,232,240,0.78)" }}
            data-testid="intervention-queue-subtitle"
          >
            {total > 0
              ? `${total} operational ${total === 1 ? "issue" : "issues"} ranked by revenue at risk and resolution leverage.`
              : "All deals operating within target parameters. Calm operational state."}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 self-end" data-testid="intervention-queue-kpis">
          <KpiTile label="Fee at risk" value={feeLabel} accent="#A78BFA" testid="kpi-fee-at-risk" />
          <KpiTile label="Critical" value={String(criticalCount || 0)} accent="#F87171" testid="kpi-critical" />
          <KpiTile label="Important" value={String(importantCount || 0)} accent="#FBBF24" testid="kpi-important" />
          <KpiTile label="Total" value={String(total || 0)} accent="#10B981" testid="kpi-total" />
        </div>
      </div>
    </header>
  );
};


const KpiTile = ({ label, value, accent, testid }) => (
  <div
    className="rounded-xl px-4 py-3"
    style={{
      background: "rgba(15,23,42,0.55)",
      border: "1px solid rgba(167,139,250,0.18)",
    }}
    data-testid={testid}
  >
    <p
      className="text-[9.5px] font-bold uppercase tracking-[0.18em]"
      style={{ color: "rgba(226,232,240,0.55)" }}
    >
      {label}
    </p>
    <p
      className="mt-1 text-[18px] font-bold tabular-nums leading-tight"
      style={{ color: accent }}
    >
      {value}
    </p>
  </div>
);


/* ─── Filter chips (Urgency) ────────────────────────────────────── */

const URGENCY_FILTERS = [
  { id: "all",           label: "All",           urgency: null },
  { id: "critical",      label: "Critical",      urgency: "critical" },
  { id: "important",     label: "Important",     urgency: "important" },
  { id: "informational", label: "Informational", urgency: "informational" },
];


const UrgencyFilters = ({ current, counts, onChange }) => (
  <div
    className="flex items-center gap-2 flex-wrap mb-4"
    data-testid="intervention-queue-filters"
  >
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em]"
          style={{ color: "#64748B" }}>
      <Filter size={11} strokeWidth={2.4} />
      Filter
    </span>
    {URGENCY_FILTERS.map((f) => {
      const count = f.urgency ? counts?.[f.urgency] ?? 0 : counts?.total ?? 0;
      const active = current === f.id;
      return (
        <button
          key={f.id}
          type="button"
          onClick={() => onChange(f.id)}
          className="inline-flex items-center gap-2 px-3.5 rounded-lg text-[12px] font-bold transition-all min-h-[40px]"
          style={
            active
              ? {
                  background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
                  color: "#FFFFFF",
                  boxShadow: "0 6px 14px -6px rgba(99,102,241,0.45)",
                }
              : {
                  background: "#FFFFFF",
                  color: "#475569",
                  border: "1px solid rgba(15,23,42,0.08)",
                }
          }
          data-testid={`intervention-filter-${f.id}`}
        >
          {f.label}
          <span
            className="inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full text-[10px] font-bold px-1.5"
            style={
              active
                ? { background: "rgba(255,255,255,0.20)", color: "#FFFFFF" }
                : { background: "rgba(15,23,42,0.06)", color: "#475569" }
            }
          >
            {count}
          </span>
        </button>
      );
    })}
  </div>
);


/* ─── Card ──────────────────────────────────────────────────────── */

const InterventionCard = ({ item, onResolve, busy }) => {
  const nav = useNavigate();
  const tone = URGENCY_TONE[item.urgency] || URGENCY_TONE.informational;
  const Icon = CATEGORY_ICON[item.category] || Sparkles;
  const fee = _money(item.impact?.fee_at_risk_gbp);
  const primary = (item.actions || []).find((a) => a.primary) || (item.actions || [])[0];

  const handlePrimary = (e) => {
    e?.preventDefault();
    if (primary?.key && item.deal_id) {
      onResolve(item, primary.key);
    } else if (item.deal_id && item.chase_blocker_id) {
      // Unclear owner: open the reviewable chase draft so the operator picks the recipient.
      nav(`/deal-rooms/${item.deal_id}?cc=1&action=chase&blocker=${item.chase_blocker_id}`);
    } else if (item.deal_id) {
      nav(`/deal-rooms/${item.deal_id}`);
    }
  };

  return (
    <article
      className="rounded-2xl bg-white transition-all hover:-translate-y-0.5"
      style={{
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 0 rgba(15,23,42,0.02)",
      }}
      data-testid={`intervention-card-${item.id}`}
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span
            className="shrink-0 w-10 h-10 rounded-xl inline-flex items-center justify-center"
            style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}
            aria-hidden="true"
          >
            <Icon size={15} strokeWidth={2.3} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em]"
                style={{ background: tone.bg, color: tone.fg }}
              >
                {tone.label}
              </span>
              <span
                className="text-[10.5px] font-bold uppercase tracking-[0.14em]"
                style={{ color: "#64748B" }}
              >
                · {item.category_label || item.category}
              </span>
              {fee ? (
                <span
                  className="ml-auto text-[12px] font-bold tabular-nums"
                  style={{ color: "#6D28D9" }}
                >
                  {fee}
                  <span className="text-[10px] font-semibold ml-1" style={{ color: "#94A3B8" }}>
                    at risk
                  </span>
                </span>
              ) : null}
            </div>
            <h3
              className="mt-1.5 text-[15px] font-bold leading-snug truncate"
              style={{ color: "#0F172A" }}
              data-testid={`intervention-card-title-${item.id}`}
            >
              {item.deal_name}
            </h3>
            <p
              className="mt-1 text-[12.5px] leading-relaxed line-clamp-2"
              style={{ color: "#475569" }}
              data-testid={`intervention-card-recommendation-${item.id}`}
            >
              {item.recommendation || "Operational drift detected."}
            </p>
            {item.evidence && item.evidence.length ? (
              <ul
                className="mt-2 flex flex-wrap gap-1.5"
                data-testid={`intervention-card-evidence-${item.id}`}
              >
                {item.evidence.slice(0, 3).map((ev, idx) => (
                  <li
                    key={idx}
                    className="px-2 py-0.5 rounded-md text-[10.5px] font-semibold"
                    style={{
                      background: "rgba(15,23,42,0.04)",
                      color: "#475569",
                      border: "1px solid rgba(15,23,42,0.06)",
                    }}
                  >
                    {ev}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrimary}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3.5 rounded-lg text-[12px] font-bold transition-all min-h-[40px]"
            style={{
              background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
              color: "#FFFFFF",
              boxShadow: "0 6px 14px -6px rgba(99,102,241,0.45)",
            }}
            data-testid={`intervention-card-primary-${item.id}`}
          >
            {busy ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <ArrowRight size={13} strokeWidth={2.4} />
            )}
            {primary?.label || (item.recipient_selection_required ? "Choose recipient" : "Open Deal Room")}
          </button>
          <button
            type="button"
            onClick={() => nav(`/deal-rooms/${item.deal_id}`)}
            className="inline-flex items-center gap-1 px-3 rounded-lg text-[12px] font-semibold transition-colors hover:bg-slate-50 min-h-[40px]"
            style={{ color: "#475569", border: "1px solid rgba(15,23,42,0.08)" }}
            data-testid={`intervention-card-secondary-${item.id}`}
          >
            Open
          </button>
        </div>
      </div>
    </article>
  );
};


/* ─── Empty state ────────────────────────────────────────────────── */

const EmptyState = () => (
  <div
    className="rounded-2xl px-5 py-8 flex items-center gap-4"
    style={{
      background: "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(5,150,105,0.04) 100%)",
      border: "1px solid rgba(16,185,129,0.22)",
    }}
    data-testid="intervention-queue-empty"
  >
    <span
      className="shrink-0 w-12 h-12 rounded-2xl inline-flex items-center justify-center"
      style={{ background: "rgba(16,185,129,0.15)", color: "#059669" }}
      aria-hidden="true"
    >
      <Check size={18} strokeWidth={2.3} />
    </span>
    <div className="min-w-0">
      <p
        className="text-[10.5px] font-bold uppercase tracking-[0.18em]"
        style={{ color: "#047857" }}
      >
        Operational state · calm
      </p>
      <p
        className="mt-1.5 text-[15px] font-semibold leading-snug"
        style={{ color: "#065F46" }}
      >
        All deals operating within target parameters.
      </p>
      <p className="mt-0.5 text-[12.5px]" style={{ color: "#0F766E" }}>
        No interventions required right now. Calm operational rhythm.
      </p>
    </div>
  </div>
);


/* ─── Page skeleton ──────────────────────────────────────────────── */

const Skeleton = () => (
  <div className="space-y-3" data-testid="intervention-queue-skeleton">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="h-[148px] rounded-2xl"
        style={{
          background: "rgba(15,23,42,0.04)",
          border: "1px solid rgba(15,23,42,0.05)",
        }}
      />
    ))}
  </div>
);


/* ─── Page ───────────────────────────────────────────────────────── */

export default function InterventionQueuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let alive = true;
    if (data === null) setLoading(true);
    else setRefreshing(true);
    (async () => {
      try {
        const { data: resp } = await api.get("/api/interventions/queue?audience=manager&limit=50");
        if (alive) setData(resp?.queue || null);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  const items = data?.items || [];
  const visible = useMemo(() => {
    return items.filter((it) => {
      if (resolvedIds.has(it.id)) return false;
      if (filter === "all") return true;
      return it.urgency === filter;
    });
  }, [items, filter, resolvedIds]);

  const counts = useMemo(() => {
    const o = { critical: 0, important: 0, informational: 0, total: 0 };
    items.forEach((it) => {
      if (resolvedIds.has(it.id)) return;
      o.total++;
      const u = it.urgency || "informational";
      if (o[u] !== undefined) o[u]++;
    });
    return o;
  }, [items, resolvedIds]);

  const totalFeeAtRisk = useMemo(() => {
    return items.reduce((sum, it) => {
      if (resolvedIds.has(it.id)) return sum;
      const v = Number(it.impact?.fee_at_risk_gbp || 0);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }, [items, resolvedIds]);

  const handleResolve = async (item, actionKey) => {
    if (!item?.deal_id || !actionKey) return;
    setBusyId(item.id);
    setResolvedIds((prev) => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
    try {
      const { data: resp } = await api.post(`/api/deals/${item.deal_id}/actions/${actionKey}`, {});
      toast.success(resp?.confirmation || "Action recorded.");
      setRefreshTick((n) => n + 1);
    } catch {
      toast.error("Could not trigger action.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen md:flex" data-testid="intervention-queue-page">
      <Sidebar />
      <main className="flex-1 relative px-4 md:px-10 py-6 md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out">
        {loading ? (
          <>
            <div
              className="h-[160px] rounded-3xl mb-6"
              style={{ background: "rgba(15,23,42,0.04)" }}
              data-testid="intervention-queue-hero-skeleton"
            />
            <Skeleton />
          </>
        ) : (
          <>
            <QueueHero
              total={counts.total}
              feeAtRiskTotal={totalFeeAtRisk}
              criticalCount={counts.critical}
              importantCount={counts.important}
              informationalCount={counts.informational}
            />
            <ChaseLadderSection />
            {items.length > 0 ? (
              <UrgencyFilters current={filter} counts={counts} onChange={setFilter} />
            ) : null}

            {visible.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="intervention-queue-grid">
                {visible.map((it) => (
                  <InterventionCard
                    key={it.id}
                    item={it}
                    onResolve={handleResolve}
                    busy={busyId === it.id}
                  />
                ))}
              </div>
            )}

            {refreshing ? (
              <p
                className="mt-4 text-[10.5px] font-bold uppercase tracking-[0.18em]"
                style={{ color: "#94A3B8" }}
              >
                Refreshing…
              </p>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
